import JSONModel from "sap/ui/model/json/JSONModel";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import BaseController from "de/realcore/realseats/controller/BaseController";
import FilterBar from "sap/ui/comp/filterbar/FilterBar";
import Table from "sap/m/Table";
import type FilterItem from "sap/ui/comp/filterbar/FilterItem";
import type SearchField from "sap/m/SearchField";
import MessageToast from "sap/m/MessageToast";
import type ColumnListItem from "sap/m/ColumnListItem";
import Log from "sap/base/Log";
import Event from "sap/ui/base/Event";
import { Employee } from "../src/types";

/**
 * Employee data structure for the EmployeeSearch view
 * We need to outsource this into a separate type file later
 */

/**
 * @namespace de.realcore.realseats.controller
 */
export default class EmployeeSearch extends BaseController {
    private _oFilterBar!: FilterBar;
    private _oTable!: Table;
    private oEmployeeModel: JSONModel;

    public onInit(): void {
        const oView = this.getView();

        this.getAllEmployees().catch(async (error: Error) => {
            Log.error("Error fetching Employees:", error);
            const oResourceBundle = await this.getResourceBundle();
            const errorMessage = error.message || "Unknown error occurred while fetching employees";
            MessageToast.show(
                `${oResourceBundle.getText("errorFetchingEmployees")}: ${errorMessage}`
            );
        });

        this._oFilterBar = oView.byId("filterbar") as FilterBar;
        this._oTable = oView.byId("tableEmployees") as Table;

        this.oEmployeeModel = new JSONModel({});
        this.getOwnerComponent().setModel(this.oEmployeeModel, "EmployeeModel");
    }

    /**
     * Fetches all employees using the new API service
     * Follows Clean Code principles with proper error handling
     */
    public async getAllEmployees(): Promise<void> {
        try {
            const apiService = this.getApiService();
            const employees = await apiService.employee.fetchAllEmployees();

            this.oEmployeeModel.setProperty("/Employees", employees);
            this.getOwnerComponent()
                .getEventBus()
                .publish("EmployeeSearch", "EmployeesLoaded", { employees });
        } catch (error) {
            Log.error("Error fetching employees:", error as Error);
            const errorMessage =
                (error as Error).message || "Unknown error occurred while fetching employees";
            MessageToast.show(`${this.getText("errorFetchingEmployees")}: ${errorMessage}`);
        }
    }

    public onSearch(): void {
        const aFilterItems = this._oFilterBar.getAllFilterItems(true) as FilterItem[];
        const aTableFilters: Filter[] = [];

        aFilterItems.forEach((oFilterItem) => {
            const sFilterName = oFilterItem.getName();
            const oFilterControl = oFilterItem.getControl() as SearchField;
            const sValue = oFilterControl.getValue()?.trim();

            if (!sValue) {
                return;
            }

            if (sFilterName === "Name") {
                // Split input into words
                const aNameParts = sValue.split(" ").filter(Boolean);
                const aWordFilters: Filter[] = [];

                aNameParts.forEach((part) => {
                    // For each word, match first OR last name
                    aWordFilters.push(
                        new Filter({
                            filters: [
                                new Filter("firstName", FilterOperator.Contains, part),
                                new Filter("lastName", FilterOperator.Contains, part)
                            ],
                            and: false // OR for first/last name
                        })
                    );
                });

                // AND between all words
                aTableFilters.push(new Filter({ filters: aWordFilters, and: true }));
            } else if (sFilterName === "Department") {
                aTableFilters.push(
                    new Filter("department/description", FilterOperator.Contains, sValue)
                );
            }
        });

        const oTableBinding = this._oTable.getBinding("items") as ListBinding;
        oTableBinding.filter(aTableFilters);
    }

    public onRowPress(oEvent: Event): void {
        const oSource: ColumnListItem = oEvent.getSource();
        const oContext = oSource.getBindingContext("EmployeeModel");

        if (oContext) {
            const oData = oContext.getObject() as Employee;

            if (oData) {
                const iId = oData.id;
                this.navTo("EmployeeDetails", { id: iId });
            } else {
                Log.error("Could not find employee data for selected row.");
            }
        } else {
            Log.error("No binding context found for selected row.");
        }
    }
}
